import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PenLine, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

/**
 * ContributorRequestForm — collapsible "Request to become a contributor"
 * form rendered on /profile and the public /blog page. Submits to
 * POST /api/blog/contributor-request which emails the admin.
 *
 * Props:
 *   - variant: "card" (default, used on /profile) | "inline" (used on /blog)
 *   - isSpanish: optional ES copy
 *   - className: extra wrapper class
 */
const ContributorRequestForm = ({ variant = 'card', isSpanish = false, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState('');
  const [experience, setExperience] = useState('');
  const [topics, setTopics] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!why.trim() || !experience.trim() || !topics.trim()) {
      toast.error(isSpanish ? 'Por favor responde las 3 preguntas' : 'Please answer all 3 questions');
      return;
    }
    setSubmitting(true);
    try {
      const { error, status } = await apiFetch('/api/blog/contributor-request', {
        method: 'POST',
        body: { why: why.trim(), experience: experience.trim(), content_topics: topics.trim() },
      });
      if (error) {
        if (status === 401) {
          toast.error(isSpanish ? 'Inicia sesión para enviar la solicitud' : 'Please sign in to submit a request');
        } else {
          toast.error(error);
        }
        return;
      }
      setSubmitted(true);
      toast.success(
        isSpanish
          ? '¡Solicitud enviada! Te responderemos pronto.'
          : 'Request sent! We\'ll be in touch soon.'
      );
    } catch {
      toast.error(isSpanish ? 'Algo salió mal' : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const Body = (
    <>
      {submitted ? (
        <div className="rounded-lg border border-[#00E5CC]/40 bg-[#00E5CC]/10 p-4 text-sm" data-testid="contributor-request-sent">
          <p className="font-medium text-foreground mb-1">
            {isSpanish ? '✓ Solicitud enviada' : '✓ Request sent'}
          </p>
          <p className="text-muted-foreground">
            {isSpanish
              ? 'Gracias por tu interés. Katie revisará tu solicitud personalmente y te enviará un correo electrónico pronto.'
              : 'Thanks for your interest. Katie personally reviews every request and will email you soon.'}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="contributor-request-form">
          <div className="space-y-1.5">
            <Label htmlFor="contrib-why" className="text-sm font-medium">
              {isSpanish ? '¿Por qué te gustaría ser colaborador?' : 'Why would you like to become a contributor?'}
            </Label>
            <Textarea
              id="contrib-why"
              value={why}
              onChange={(e) => setWhy(e.target.value.slice(0, 2000))}
              placeholder={isSpanish
                ? 'Cuéntanos qué te motiva...'
                : 'Tell us what draws you to this community...'}
              rows={3}
              maxLength={2000}
              data-testid="contributor-why-input"
            />
            <span className="text-xs text-muted-foreground tabular-nums">{why.length} / 2000</span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contrib-experience" className="text-sm font-medium">
              {isSpanish ? '¿Qué experiencia tienes?' : 'What\'s your experience?'}
            </Label>
            <Textarea
              id="contrib-experience"
              value={experience}
              onChange={(e) => setExperience(e.target.value.slice(0, 2000))}
              placeholder={isSpanish
                ? 'Ej: 10 años como coach de TDAH, MSW, escribo en mi propio blog…'
                : 'E.g. 10 years as an ADHD coach, MSW, blog of my own…'}
              rows={3}
              maxLength={2000}
              data-testid="contributor-experience-input"
            />
            <span className="text-xs text-muted-foreground tabular-nums">{experience.length} / 2000</span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contrib-topics" className="text-sm font-medium">
              {isSpanish ? '¿Qué tipo de contenido publicarías?' : 'What kind of content would you post?'}
            </Label>
            <Textarea
              id="contrib-topics"
              value={topics}
              onChange={(e) => setTopics(e.target.value.slice(0, 2000))}
              placeholder={isSpanish
                ? 'Ej: estrategias de comunicación, historias de TDAH/Autismo, scripts para situaciones laborales…'
                : 'E.g. communication strategies, ADHD/Autism lived experience, scripts for hard workplace conversations…'}
              rows={3}
              maxLength={2000}
              data-testid="contributor-topics-input"
            />
            <span className="text-xs text-muted-foreground tabular-nums">{topics.length} / 2000</span>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-semibold"
            data-testid="submit-contributor-request-btn"
          >
            {submitting
              ? (isSpanish ? 'Enviando…' : 'Submitting…')
              : (isSpanish ? 'Enviar solicitud' : 'Submit request')}
          </Button>
        </form>
      )}
    </>
  );

  if (variant === 'inline') {
    return (
      <div className={className} data-testid="contributor-request-inline">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-medium text-[#00A896] hover:text-[#008977] transition-colors"
          data-testid="toggle-contributor-form-btn"
        >
          <PenLine className="h-4 w-4" />
          {isSpanish ? '¿Quieres escribir para el blog?' : 'Want to write for the blog?'}
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {open && <div className="mt-4 p-4 rounded-lg border bg-card">{Body}</div>}
      </div>
    );
  }

  // Default card variant
  return (
    <Card className={`mb-6 ${className}`} data-testid="contributor-request-card">
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 text-left"
          data-testid="toggle-contributor-form-btn"
        >
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-5 w-5 text-[#00E5CC]" />
              {isSpanish ? 'Solicita ser colaborador del blog' : 'Request to become a blog contributor'}
            </CardTitle>
            <CardDescription className="mt-1">
              {isSpanish
                ? 'Comparte tu voz con la comunidad neurodivergente.'
                : 'Share your voice with the neurodivergent community.'}
            </CardDescription>
          </div>
          {open ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
        </button>
      </CardHeader>
      {open && <CardContent>{Body}</CardContent>}
    </Card>
  );
};

export default ContributorRequestForm;
